import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MOVE_OUT_CATEGORY_LABELS,
  MOVE_OUT_ROLE_LABELS,
  ownerLaborCents,
  type LeaseDTO,
  type MailingAddressInput,
  type MoveOutAddressKind,
  type MoveOutCategoryInput,
  type MoveOutDeductionCreateInput,
  type MoveOutDeductionDTO,
  type MoveOutDocumentRoleInput,
  type MoveOutRecipientDTO,
  type MoveOutReportDTO,
  type MoveOutReportDocumentDTO,
} from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { formatCents } from '../../lib/money';
import { formatDate } from '../../lib/date';
import { blobToBase64, isImageFile, prepareMoveOutUpload } from '../../lib/upload';
import { useAuth } from '../../providers/AuthProvider';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { Dialog } from '../../components/a11y/Dialog';
import { SignAndSend, deliveryKey } from '../../components/moveout/SignAndSend';

/**
 * Move-out report + § 1950.5 itemized security-deposit statement (0.24.0). One page per lease
 * (`/admin/leases/:leaseId/move-out`, linked from LeaseDetail): possession date (starts the
 * 21-day clock — the due date + days-remaining badge are computed server-side), deposit held,
 * statement recipients with mailing addresses, the itemized deductions (with a receipt upload per
 * row), photos by AB 2801 phase, live totals, the finalize pre-flight (blockers + warnings), a
 * draft PDF preview, and Finalize — after which everything is read-only and the statement can be
 * downloaded or shared with the tenants. Every mutation returns the refreshed report, which is
 * written straight into the react-query cache.
 *
 * 0.27.0 — a finalized report gains a "Sign & send" section (components/moveout/SignAndSend.tsx):
 * the owner signs the statement, enters each recipient's delivery email, and emails the signed copy.
 *
 * 0.28.1 — the OWNER can "Reopen for amendment" a finalized report (required reason + a plain
 * warning); the report returns to an editable draft as the next revision and the superseded
 * statement is listed under "Revision history".
 */

const CATEGORIES: MoveOutCategoryInput[] = [
  'unpaid_rent',
  'cleaning',
  'repair',
  'key_replacement',
  'utilities',
  'other',
];

const PHOTO_ROLES: MoveOutDocumentRoleInput[] = ['move_in_photo', 'move_out_photo', 'post_repair_photo'];

function reportKey(leaseId: string) {
  return ['admin', 'move-out', leaseId] as const;
}

/** Parse a dollars string to integer cents. Blank/NaN → null. */
function dollarsToCents(v: string): number | null {
  const t = v.trim();
  if (t === '') return null;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
}

function centsToDollars(cents: number): string {
  return (cents / 100).toFixed(2);
}

function opt(v: string): string | undefined {
  const t = v.trim();
  return t.length > 0 ? t : undefined;
}

/**
 * Build the attach payload for a file: images go up as a ≤2048 px JPEG RENDITION (`upload`) plus
 * the untouched ORIGINAL (`original`, EXIF intact) — the server stores both and links them; when a
 * rendition wouldn't be smaller, only the original is sent. `capturedAt` = the explicit override,
 * else EXIF DateTimeOriginal, else the file's last-modified time (images only).
 */
async function toAttachPayload(file: File, capturedOverride?: string | null) {
  const prepared = await prepareMoveOutUpload(file);
  const upload = {
    filename: prepared.primary.filename,
    contentType: prepared.primary.contentType,
    contentBase64: await blobToBase64(prepared.primary.blob),
    capturedAt: capturedOverride ?? (isImageFile(file) ? prepared.capturedAt : null),
  };
  const original = prepared.original
    ? {
        filename: prepared.original.filename,
        contentType: prepared.original.contentType,
        contentBase64: await blobToBase64(prepared.original.blob),
      }
    : undefined;
  return { upload, original };
}

/** Save a Blob as a file (a download, not a navigation — no CSP/PDF-viewer surprises). */
function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function DueBadge({ report }: { report: MoveOutReportDTO }) {
  if (report.status === 'finalized') return <span className="badge">Statement finalized</span>;
  if (report.daysRemaining == null) return <span className="badge">No possession date yet</span>;
  const d = report.daysRemaining;
  const text = d < 0 ? `Overdue by ${-d} day${d === -1 ? '' : 's'}` : d === 0 ? 'Due today' : `${d} day${d === 1 ? '' : 's'} left`;
  const style =
    d < 0
      ? { background: 'var(--err-bg)', color: 'var(--err)' }
      : d <= 5
        ? { background: 'var(--warn-bg)', color: 'var(--warn)' }
        : undefined;
  return (
    <span className="badge" style={style}>
      {text}
    </span>
  );
}

/* ------------------------------------------------------------------ *
 * Start a report.                                                     *
 * ------------------------------------------------------------------ */

function StartReport({ lease }: { lease: LeaseDTO }) {
  const queryClient = useQueryClient();
  const [possession, setPossession] = useState('');
  const [deposit, setDeposit] = useState(lease.depositCents != null ? centsToDollars(lease.depositCents) : '');
  const start = useMutation({
    mutationFn: () => {
      const depositHeldCents = dollarsToCents(deposit);
      if (depositHeldCents != null && depositHeldCents < 0) throw new Error('Enter a valid deposit amount.');
      return api.moveOut.create({
        leaseId: lease.id,
        possessionReturnedDate: opt(possession) ?? null,
        depositHeldCents: depositHeldCents ?? undefined,
      });
    },
    onSuccess: (report) => queryClient.setQueryData(reportKey(lease.id), { report }),
  });
  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        start.mutate();
      }}
    >
      <p>
        No move-out report yet. Start one to build the itemized security-deposit statement required by Civil Code §
        1950.5 within 21 days after you regain possession.
      </p>
      {start.isError && <StatusMessage tone="err">{errorMessage(start.error)}</StatusMessage>}
      <div>
        <label htmlFor="mo-start-possession">Date you regained possession (optional now)</label>
        <input id="mo-start-possession" type="date" value={possession} onChange={(e) => setPossession(e.target.value)} />
        <p className="field-hint">The 21-day clock runs from this date — not the lease end date.</p>
      </div>
      <div>
        <label htmlFor="mo-start-deposit">Security deposit held (dollars)</label>
        <input
          id="mo-start-deposit"
          type="number"
          step="0.01"
          min="0"
          value={deposit}
          onChange={(e) => setDeposit(e.target.value)}
        />
      </div>
      <div>
        <button type="submit" disabled={start.isPending}>
          {start.isPending ? 'Starting…' : 'Start move-out report'}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ *
 * Report core (possession date, deposit, notes).                      *
 * ------------------------------------------------------------------ */

function CoreForm({ report, onSaved }: { report: MoveOutReportDTO; onSaved: (r: MoveOutReportDTO) => void }) {
  const [possession, setPossession] = useState(report.possessionReturnedDate ?? '');
  const [deposit, setDeposit] = useState(centsToDollars(report.depositHeldCents));
  const [notes, setNotes] = useState(report.ownerNotes ?? '');
  const save = useMutation({
    mutationFn: () => {
      const depositHeldCents = dollarsToCents(deposit);
      if (depositHeldCents == null || depositHeldCents < 0) throw new Error('Enter a valid deposit amount.');
      return api.moveOut.update(report.id, {
        possessionReturnedDate: opt(possession) ?? null,
        depositHeldCents,
        ownerNotes: opt(notes) ?? null,
      });
    },
    onSuccess: onSaved,
  });
  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
    >
      {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
      {save.isSuccess && <StatusMessage tone="ok">Saved.</StatusMessage>}
      <div>
        <label htmlFor="mo-possession">Date you regained possession</label>
        <input id="mo-possession" type="date" value={possession} onChange={(e) => setPossession(e.target.value)} />
        <p className="field-hint">
          Statement due: <strong>{formatDate(report.statementDueDate)}</strong> (21 calendar days after possession).
        </p>
      </div>
      <div>
        <label htmlFor="mo-deposit">Security deposit held (dollars)</label>
        <input
          id="mo-deposit"
          type="number"
          step="0.01"
          min="0"
          value={deposit}
          onChange={(e) => setDeposit(e.target.value)}
        />
      </div>
      <div>
        <label htmlFor="mo-notes">Owner notes (internal — never printed on the statement)</label>
        <textarea id="mo-notes" rows={3} maxLength={2000} value={notes} onChange={(e) => setNotes(e.target.value)} />
      </div>
      <div>
        <button type="submit" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ *
 * Recipients.                                                         *
 * ------------------------------------------------------------------ */

function RecipientDialog({
  report,
  lease,
  recipient,
  onClose,
  onSaved,
}: {
  report: MoveOutReportDTO;
  lease: LeaseDTO;
  recipient: MoveOutRecipientDTO | null;
  onClose: () => void;
  onSaved: (r: MoveOutReportDTO) => void;
}) {
  const taken = new Set(report.recipients.map((r) => r.tenantId));
  const available = lease.tenants.filter((t) => !taken.has(t.tenantId) && t.role !== 'guarantor');
  const [tenantId, setTenantId] = useState(recipient?.tenantId ?? available[0]?.tenantId ?? '');
  const [kind, setKind] = useState<MoveOutAddressKind>(recipient?.addressKind ?? 'forwarding');
  const [line1, setLine1] = useState(recipient?.address?.line1 ?? '');
  const [line2, setLine2] = useState(recipient?.address?.line2 ?? '');
  const [city, setCity] = useState(recipient?.address?.city ?? '');
  const [state, setState] = useState(recipient?.address?.state ?? 'CA');
  const [postal, setPostal] = useState(recipient?.address?.postalCode ?? '');

  const save = useMutation({
    mutationFn: () => {
      const anyAddress = [line1, city, postal].some((v) => v.trim() !== '');
      let address: MailingAddressInput | null = null;
      if (anyAddress) {
        if (!opt(line1) || !opt(city) || !opt(state) || !opt(postal)) {
          throw new Error('Enter street, city, state, and ZIP — or leave the address blank for now.');
        }
        address = { line1: line1.trim(), line2: opt(line2) ?? null, city: city.trim(), state: state.trim(), postalCode: postal.trim() };
      }
      if (recipient) return api.moveOut.updateRecipient(report.id, recipient.id, { addressKind: kind, address });
      if (!tenantId) throw new Error('Choose a tenant.');
      return api.moveOut.addRecipient(report.id, { tenantId, addressKind: kind, address });
    },
    onSuccess: (r) => {
      onSaved(r);
      onClose();
    },
  });

  return (
    <Dialog title={recipient ? `Mailing address — ${recipient.tenantName}` : 'Add recipient'} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        {!recipient && (
          <div>
            <label htmlFor="rc-tenant">Tenant on this lease</label>
            {available.length === 0 ? (
              <p className="muted">Every tenant on the lease is already a recipient.</p>
            ) : (
              <select id="rc-tenant" value={tenantId} onChange={(e) => setTenantId(e.target.value)}>
                {available.map((t) => (
                  <option key={t.tenantId} value={t.tenantId}>
                    {t.tenant ? `${t.tenant.firstName} ${t.tenant.lastName}` : t.tenantId}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}
        <div>
          <label htmlFor="rc-kind">Address type</label>
          <select id="rc-kind" value={kind} onChange={(e) => setKind(e.target.value as MoveOutAddressKind)}>
            <option value="forwarding">Forwarding address (provided by the tenant)</option>
            <option value="last_known">Last known address</option>
          </select>
        </div>
        <div>
          <label htmlFor="rc-line1">Street</label>
          <input id="rc-line1" value={line1} maxLength={200} onChange={(e) => setLine1(e.target.value)} autoComplete="off" />
        </div>
        <div>
          <label htmlFor="rc-line2">Apt / unit (optional)</label>
          <input id="rc-line2" value={line2} maxLength={200} onChange={(e) => setLine2(e.target.value)} autoComplete="off" />
        </div>
        <div className="row">
          <div style={{ flex: 2 }}>
            <label htmlFor="rc-city">City</label>
            <input id="rc-city" value={city} maxLength={100} onChange={(e) => setCity(e.target.value)} autoComplete="off" />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="rc-state">State</label>
            <input id="rc-state" value={state} maxLength={40} onChange={(e) => setState(e.target.value)} autoComplete="off" />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="rc-zip">ZIP</label>
            <input id="rc-zip" value={postal} maxLength={20} onChange={(e) => setPostal(e.target.value)} autoComplete="off" />
          </div>
        </div>
        <div className="row">
          <button type="submit" disabled={save.isPending || (!recipient && available.length === 0)}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Reopen a finalized report for amendment (0.28.1, owner only).       *
 * ------------------------------------------------------------------ */

const REOPEN_REASON_MIN = 10;
const REOPEN_REASON_MAX = 500;

function ReopenDialog({
  report,
  onClose,
  onReopened,
}: {
  report: MoveOutReportDTO;
  onClose: () => void;
  onReopened: (r: MoveOutReportDTO) => void;
}) {
  const [reason, setReason] = useState('');
  const reopen = useMutation({
    mutationFn: () => api.moveOut.reopen(report.id, { reason: reason.trim() }),
    onSuccess: (r) => {
      onReopened(r);
      onClose();
    },
  });
  const tooShort = reason.trim().length < REOPEN_REASON_MIN;
  return (
    <Dialog title="Reopen for amendment" onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          if (!tooShort) reopen.mutate();
        }}
      >
        {reopen.isError && <StatusMessage tone="err">{errorMessage(reopen.error)}</StatusMessage>}
        <StatusMessage tone="warn">
          The current statement stays on file as superseded. Any signature is cleared. The report goes back to an
          editable draft and must be finalized (and signed) again before it can be sent.
        </StatusMessage>
        <div>
          <label htmlFor="ro-reason">Reason for the amendment (required)</label>
          <textarea
            id="ro-reason"
            rows={3}
            required
            minLength={REOPEN_REASON_MIN}
            maxLength={REOPEN_REASON_MAX}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            aria-describedby="ro-reason-hint"
          />
          <p id="ro-reason-hint" className="muted">
            At least {REOPEN_REASON_MIN} characters — for example “Add the painting estimate received after finalizing”.
            Recorded with the superseded statement and in the audit log.
          </p>
        </div>
        <div className="row">
          <button type="submit" disabled={reopen.isPending || tooShort}>
            {reopen.isPending ? 'Reopening…' : 'Reopen for amendment'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/* ------------------------------------------------------------------ *
 * Deductions.                                                         *
 * ------------------------------------------------------------------ */

function DeductionDialog({
  report,
  deduction,
  onClose,
  onSaved,
}: {
  report: MoveOutReportDTO;
  deduction: MoveOutDeductionDTO | null;
  onClose: () => void;
  onSaved: (r: MoveOutReportDTO) => void;
}) {
  const [category, setCategory] = useState<MoveOutCategoryInput>(deduction?.category ?? 'cleaning');
  const [description, setDescription] = useState(deduction?.description ?? '');
  const [amount, setAmount] = useState(deduction ? centsToDollars(deduction.amountCents) : '');
  const [isEstimate, setIsEstimate] = useState(deduction?.isEstimate ?? false);
  const [vendorInfo, setVendorInfo] = useState(deduction?.vendorInfo ?? '');
  const [ownerLabor, setOwnerLabor] = useState(deduction?.ownerLaborMinutes != null);
  const [hours, setHours] = useState(
    deduction?.ownerLaborMinutes != null ? String(deduction.ownerLaborMinutes / 60) : '',
  );
  const [rate, setRate] = useState(
    deduction?.ownerLaborRateCents != null ? centsToDollars(deduction.ownerLaborRateCents) : '',
  );

  const laborMinutes = ownerLabor ? Math.round(Number.parseFloat(hours) * 60) : null;
  const laborRate = ownerLabor ? dollarsToCents(rate) : null;
  const laborValid = !ownerLabor || (laborMinutes != null && laborMinutes > 0 && laborRate != null && laborRate > 0);

  const save = useMutation({
    mutationFn: () => {
      const amountCents = dollarsToCents(amount);
      if (amountCents == null || amountCents <= 0) throw new Error('Enter an amount greater than zero.');
      if (!opt(description)) throw new Error('Describe the deduction.');
      if (!laborValid) throw new Error('Enter the hours and hourly rate for owner/employee labor.');
      const body: MoveOutDeductionCreateInput = {
        category,
        description: description.trim(),
        amountCents,
        isEstimate,
        vendorInfo: opt(vendorInfo) ?? null,
        ownerLaborMinutes: ownerLabor ? laborMinutes : null,
        ownerLaborRateCents: ownerLabor ? laborRate : null,
      };
      return deduction
        ? api.moveOut.updateDeduction(report.id, deduction.id, body)
        : api.moveOut.addDeduction(report.id, body);
    },
    onSuccess: (r) => {
      onSaved(r);
      onClose();
    },
  });

  return (
    <Dialog title={deduction ? 'Edit deduction' : 'Add deduction'} onClose={onClose}>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {save.isError && <StatusMessage tone="err">{errorMessage(save.error)}</StatusMessage>}
        <div>
          <label htmlFor="dd-category">Category</label>
          <select id="dd-category" value={category} onChange={(e) => setCategory(e.target.value as MoveOutCategoryInput)}>
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {MOVE_OUT_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="dd-description">Description (printed on the statement)</label>
          <textarea
            id="dd-description"
            rows={3}
            maxLength={1000}
            value={description}
            required
            onChange={(e) => setDescription(e.target.value)}
          />
          <p className="field-hint">
            Be specific — what was damaged or dirty beyond ordinary wear and tear, and what was done.
          </p>
        </div>
        <div>
          <label htmlFor="dd-amount">Amount (dollars)</label>
          <input id="dd-amount" type="number" step="0.01" min="0.01" value={amount} required onChange={(e) => setAmount(e.target.value)} />
        </div>
        <div className="row">
          <input
            id="dd-labor"
            type="checkbox"
            style={{ width: 'auto' }}
            checked={ownerLabor}
            onChange={(e) => setOwnerLabor(e.target.checked)}
          />
          <label htmlFor="dd-labor" style={{ margin: 0 }}>
            Work done by me or my employee (the statement must show time and hourly rate)
          </label>
        </div>
        {ownerLabor && (
          <div className="row">
            <div style={{ flex: 1 }}>
              <label htmlFor="dd-hours">Hours</label>
              <input id="dd-hours" type="number" step="0.25" min="0.25" value={hours} onChange={(e) => setHours(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <label htmlFor="dd-rate">Hourly rate (dollars)</label>
              <input id="dd-rate" type="number" step="0.01" min="0.01" value={rate} onChange={(e) => setRate(e.target.value)} />
            </div>
            {laborValid && laborMinutes != null && laborRate != null && (
              <p className="field-hint" style={{ flexBasis: '100%' }}>
                Time × rate = {formatCents(ownerLaborCents(laborMinutes, laborRate))}{' '}
                <button type="button" className="secondary" onClick={() => setAmount(centsToDollars(ownerLaborCents(laborMinutes, laborRate)))}>
                  Use as amount
                </button>
              </p>
            )}
          </div>
        )}
        <div className="row">
          <input
            id="dd-estimate"
            type="checkbox"
            style={{ width: 'auto' }}
            checked={isEstimate}
            onChange={(e) => setIsEstimate(e.target.checked)}
          />
          <label htmlFor="dd-estimate" style={{ margin: 0 }}>
            Good-faith estimate (work not done / bill not received within 21 days — receipts due within 14 days of completion)
          </label>
        </div>
        {isEstimate && (
          <div>
            <label htmlFor="dd-vendor">Who will do the work (name, address, phone)</label>
            <input id="dd-vendor" value={vendorInfo} maxLength={500} onChange={(e) => setVendorInfo(e.target.value)} />
          </div>
        )}
        <div className="row">
          <button type="submit" disabled={save.isPending}>
            {save.isPending ? 'Saving…' : 'Save deduction'}
          </button>
          <button type="button" className="secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Dialog>
  );
}

/** Per-deduction receipt/invoice upload (a compact inline file picker). */
function ReceiptUpload({
  report,
  deduction,
  onSaved,
}: {
  report: MoveOutReportDTO;
  deduction: MoveOutDeductionDTO;
  onSaved: (r: MoveOutReportDTO) => void;
}) {
  const upload = useMutation({
    mutationFn: async (file: File) => {
      const role: MoveOutDocumentRoleInput = /invoice/i.test(file.name) ? 'invoice' : 'receipt';
      const { upload: up, original } = await toAttachPayload(file);
      return api.moveOut.attachDocument(report.id, { role, deductionId: deduction.id, upload: up, original });
    },
    onSuccess: onSaved,
  });
  const inputId = `rcpt-${deduction.id}`;
  return (
    <div>
      <label htmlFor={inputId} className="visually-hidden">
        Upload receipt for {deduction.description}
      </label>
      <input
        id={inputId}
        type="file"
        accept="image/*,application/pdf"
        disabled={upload.isPending}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
          e.target.value = '';
        }}
        style={{ maxWidth: '14rem' }}
      />
      {upload.isPending && <span className="muted"> Uploading…</span>}
      {upload.isError && <StatusMessage tone="err">{errorMessage(upload.error)}</StatusMessage>}
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * Evidence: photos + other documents.                                 *
 * ------------------------------------------------------------------ */

/** A thumbnail of a stored image document (mints a short-lived signed URL). */
function DocThumb({ att }: { att: MoveOutReportDocumentDTO }) {
  const doc = att.document;
  const isImage = doc != null && /^image\/(jpeg|png|gif|webp)/i.test(doc.contentType);
  const url = useQuery({
    queryKey: ['admin', 'doc-url', att.documentId],
    queryFn: () => api.documents.signedUrl(att.documentId, { ttlSeconds: 3600 }),
    enabled: isImage,
    staleTime: 50 * 60 * 1000,
  });
  if (!doc) return <p className="muted">Document removed from the store.</p>;
  if (!isImage) return <p className="muted">{doc.filename}</p>;
  if (!url.data) return <p className="muted">Loading preview…</p>;
  const alt = [att.caption, att.room, doc.filename].filter(Boolean).join(' — ');
  return (
    <img
      src={url.data.url}
      alt={alt}
      style={{ width: '100%', height: '9rem', objectFit: 'cover', borderRadius: 'var(--radius)' }}
    />
  );
}

function PhotoUpload({ report, onSaved }: { report: MoveOutReportDTO; onSaved: (r: MoveOutReportDTO) => void }) {
  const [role, setRole] = useState<MoveOutDocumentRoleInput>('move_out_photo');
  const [room, setRoom] = useState('');
  const [caption, setCaption] = useState('');
  const [taken, setTaken] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async () => {
      if (files.length === 0) throw new Error('Choose one or more photos.');
      let latest: MoveOutReportDTO | null = null;
      for (let i = 0; i < files.length; i++) {
        const file = files[i]!;
        setProgress(`Uploading ${i + 1} of ${files.length}…`);
        const { upload: up, original } = await toAttachPayload(file, taken ? `${taken}T12:00:00Z` : null);
        latest = await api.moveOut.attachDocument(report.id, {
          role,
          room: opt(room) ?? null,
          caption: opt(caption) ?? null,
          upload: up,
          original,
        });
        onSaved(latest);
      }
      return latest!;
    },
    onSuccess: () => {
      setFiles([]);
      setCaption('');
      setProgress(null);
    },
    onError: () => setProgress(null),
  });

  return (
    <form
      className="card stack"
      onSubmit={(e) => {
        e.preventDefault();
        upload.mutate();
      }}
    >
      <h3>Upload photos</h3>
      {upload.isError && <StatusMessage tone="err">{errorMessage(upload.error)}</StatusMessage>}
      {progress && <StatusMessage>{progress}</StatusMessage>}
      <div className="row">
        <div style={{ flex: 1, minWidth: '12rem' }}>
          <label htmlFor="ph-role">Phase</label>
          <select id="ph-role" value={role} onChange={(e) => setRole(e.target.value as MoveOutDocumentRoleInput)}>
            {PHOTO_ROLES.map((r) => (
              <option key={r} value={r}>
                {MOVE_OUT_ROLE_LABELS[r]}
              </option>
            ))}
          </select>
        </div>
        <div style={{ flex: 1, minWidth: '12rem' }}>
          <label htmlFor="ph-room">Room / area (optional)</label>
          <input id="ph-room" value={room} maxLength={100} onChange={(e) => setRoom(e.target.value)} />
        </div>
        <div style={{ flex: 1, minWidth: '12rem' }}>
          <label htmlFor="ph-taken">Date taken (optional)</label>
          <input id="ph-taken" type="date" value={taken} onChange={(e) => setTaken(e.target.value)} />
        </div>
      </div>
      <div>
        <label htmlFor="ph-caption">Caption (optional, applied to each photo)</label>
        <input id="ph-caption" value={caption} maxLength={500} onChange={(e) => setCaption(e.target.value)} />
      </div>
      <div>
        <label htmlFor="ph-files">Photos</label>
        <input
          id="ph-files"
          type="file"
          accept="image/*"
          multiple
          onChange={(e) => setFiles(Array.from(e.target.files ?? []))}
        />
        <p className="field-hint">
          Each photo&apos;s original is kept unchanged; a smaller copy (≤2048 px JPEG) is made for the gallery and
          the statement. iPhone HEIC photos are converted for the copy. Without a date above, the photo&apos;s
          own capture date (EXIF) is used, else the file&apos;s last-modified date.
        </p>
      </div>
      <div>
        <button type="submit" disabled={upload.isPending || files.length === 0}>
          {upload.isPending ? 'Uploading…' : `Upload ${files.length || ''} photo${files.length === 1 ? '' : 's'}`}
        </button>
      </div>
    </form>
  );
}

/* ------------------------------------------------------------------ *
 * The page.                                                           *
 * ------------------------------------------------------------------ */

/** Route entry: resolves the caller's role (finalize + portal sharing are OWNER-only). */
export function AdminMoveOut() {
  const { principal } = useAuth();
  return <MoveOutPage isOwner={principal?.role === 'owner'} ownerEmail={principal?.email} />;
}

/** The page body. `isOwner` gates Finalize + the share toggle (the API enforces it too: staff 403). */
export function MoveOutPage({ isOwner, ownerEmail }: { isOwner: boolean; ownerEmail?: string }) {
  const { leaseId = '' } = useParams();
  const queryClient = useQueryClient();
  const leaseQuery = useQuery({
    queryKey: ['admin', 'lease', leaseId],
    queryFn: () => api.leases.get(leaseId),
    enabled: leaseId !== '',
  });
  const reportQuery = useQuery({
    queryKey: reportKey(leaseId),
    queryFn: () => api.moveOut.forLease(leaseId),
    enabled: leaseId !== '',
  });
  const [dialog, setDialog] = useState<
    | { kind: 'recipient'; recipient: MoveOutRecipientDTO | null }
    | { kind: 'deduction'; deduction: MoveOutDeductionDTO | null }
    | null
  >(null);
  const [finalizeWarnings, setFinalizeWarnings] = useState<string[] | null>(null);
  const [reopenOpen, setReopenOpen] = useState(false);

  const setReport = (report: MoveOutReportDTO) => queryClient.setQueryData(reportKey(leaseId), { report });

  const removeRecipient = useMutation({
    mutationFn: (v: { reportId: string; id: string }) => api.moveOut.removeRecipient(v.reportId, v.id),
    onSuccess: setReport,
  });
  const removeDeduction = useMutation({
    mutationFn: (v: { reportId: string; id: string }) => api.moveOut.removeDeduction(v.reportId, v.id),
    onSuccess: setReport,
  });
  const detach = useMutation({
    mutationFn: (v: { reportId: string; id: string }) => api.moveOut.detachDocument(v.reportId, v.id),
    onSuccess: setReport,
  });
  const preview = useMutation({
    mutationFn: (reportId: string) => api.moveOut.previewPdf(reportId),
    onSuccess: (blob) => saveBlob(blob, 'deposit-statement-preview.pdf'),
  });
  const finalize = useMutation({
    mutationFn: (reportId: string) => api.moveOut.finalize(reportId),
    onSuccess: (resp) => {
      setReport(resp.report);
      setFinalizeWarnings(resp.warnings.map((w) => w.message));
      void queryClient.invalidateQueries({ queryKey: ['admin', 'documents'] });
    },
  });
  const download = useMutation({
    mutationFn: (docId: string) => api.documents.signedUrl(docId),
    onSuccess: (resp) => window.open(resp.url, '_blank', 'noopener'),
  });
  const share = useMutation({
    mutationFn: (v: { reportId: string; visibility: 'owner_only' | 'tenant_visible' }) =>
      api.moveOut.setStatementVisibility(v.reportId, { visibility: v.visibility }),
    onSuccess: setReport,
  });
  const discard = useMutation({
    mutationFn: (reportId: string) => api.moveOut.remove(reportId),
    onSuccess: () => queryClient.setQueryData(reportKey(leaseId), { report: null }),
  });

  if (leaseId === '') return <ErrorState message="Missing lease id." />;
  if (leaseQuery.isLoading || reportQuery.isLoading) return <Loading label="Loading move-out report…" />;
  if (leaseQuery.isError || !leaseQuery.data)
    return <ErrorState message={errorMessage(leaseQuery.error)} onRetry={() => void leaseQuery.refetch()} />;
  if (reportQuery.isError || !reportQuery.data)
    return <ErrorState message={errorMessage(reportQuery.error)} onRetry={() => void reportQuery.refetch()} />;

  const lease = leaseQuery.data;
  const report = reportQuery.data.report;
  const locked = report?.status === 'finalized';

  const header = (
    <>
      <p>
        <Link to={`/admin/leases/${encodeURIComponent(lease.id)}`}>← Lease</Link>
      </p>
      <h1 id="mo-heading">Move-out &amp; deposit statement</h1>
      <p className="subtitle">
        {lease.unitLabel ?? lease.unitId} ·{' '}
        {lease.tenants.map((t) => (t.tenant ? `${t.tenant.firstName} ${t.tenant.lastName}` : t.tenantId)).join(', ')}
      </p>
    </>
  );

  if (!report) {
    return (
      <section aria-labelledby="mo-heading" className="stack">
        {header}
        <StartReport lease={lease} />
      </section>
    );
  }

  const photos = report.documents.filter((d) => d.role.endsWith('_photo'));
  const otherDocs = report.documents.filter((d) => !d.role.endsWith('_photo'));
  const deductionLabel = (id: string | null) =>
    id ? (report.deductions.find((d) => d.id === id)?.description ?? '—') : '—';

  return (
    <>
      <section aria-labelledby="mo-heading" className="stack">
        {header}
        <div className="row">
          <span className="badge">{report.status === 'finalized' ? 'Finalized' : 'Draft'}</span>
          {report.revision > 1 && (
            <span className="badge">
              {report.amended ? 'Amended statement — ' : ''}Revision {report.revision}
            </span>
          )}
          <DueBadge report={report} />
          {report.statementDueDate && <span className="muted">Statement due {formatDate(report.statementDueDate)}</span>}
        </div>
        {locked ? (
          <dl className="card kv-grid">
            <div>
              <dt className="muted">Possession returned</dt>
              <dd>{formatDate(report.possessionReturnedDate)}</dd>
            </div>
            <div>
              <dt className="muted">Deposit held</dt>
              <dd>{formatCents(report.depositHeldCents)}</dd>
            </div>
            <div>
              <dt className="muted">Finalized</dt>
              <dd>{formatDate(report.finalizedAt)}</dd>
            </div>
          </dl>
        ) : (
          <CoreForm key={report.id} report={report} onSaved={setReport} />
        )}
      </section>

      <section aria-labelledby="mo-recipients" className="stack">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 id="mo-recipients">Recipients</h2>
          {!locked && (
            <button type="button" onClick={() => setDialog({ kind: 'recipient', recipient: null })}>
              Add recipient
            </button>
          )}
        </div>
        {removeRecipient.isError && <StatusMessage tone="err">{errorMessage(removeRecipient.error)}</StatusMessage>}
        {report.recipients.length === 0 ? (
          <p className="muted">Add each tenant with the forwarding (or last known) address the statement is mailed to.</p>
        ) : (
          <div className="table-wrap card" style={{ padding: 0 }}>
            <table>
              <caption className="visually-hidden">Statement recipients</caption>
              <thead>
                <tr>
                  <th scope="col">Tenant</th>
                  <th scope="col">Mailing address</th>
                  {!locked && <th scope="col">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {report.recipients.map((r) => (
                  <tr key={r.id}>
                    <td>{r.tenantName}</td>
                    <td>
                      {r.address ? (
                        <>
                          {r.address.line1}
                          {r.address.line2 ? `, ${r.address.line2}` : ''}, {r.address.city}, {r.address.state}{' '}
                          {r.address.postalCode}{' '}
                          <span className="muted">({r.addressKind === 'forwarding' ? 'forwarding' : 'last known'})</span>
                        </>
                      ) : (
                        <span className="muted">No address yet</span>
                      )}
                      {r.deliveryEmailMasked && (
                        <>
                          <br />
                          <span className="muted">Email: {r.deliveryEmailMasked}</span>
                        </>
                      )}
                    </td>
                    {!locked && (
                      <td>
                        <div className="row">
                          <button type="button" className="secondary" onClick={() => setDialog({ kind: 'recipient', recipient: r })}>
                            Edit<span className="visually-hidden"> {r.tenantName}</span>
                          </button>
                          <button
                            type="button"
                            className="secondary"
                            disabled={removeRecipient.isPending}
                            onClick={() => removeRecipient.mutate({ reportId: report.id, id: r.id })}
                          >
                            Remove<span className="visually-hidden"> {r.tenantName}</span>
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="mo-deductions" className="stack">
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline' }}>
          <h2 id="mo-deductions">Deductions</h2>
          {!locked && (
            <button type="button" onClick={() => setDialog({ kind: 'deduction', deduction: null })}>
              Add deduction
            </button>
          )}
        </div>
        {removeDeduction.isError && <StatusMessage tone="err">{errorMessage(removeDeduction.error)}</StatusMessage>}
        {report.deductions.length === 0 ? (
          <p className="muted">No deductions — the full deposit will be returned.</p>
        ) : (
          <div className="table-wrap card" style={{ padding: 0 }}>
            <table>
              <caption className="visually-hidden">Itemized deductions</caption>
              <thead>
                <tr>
                  <th scope="col">Category</th>
                  <th scope="col">Description</th>
                  <th scope="col" className="num">
                    Amount
                  </th>
                  <th scope="col">Receipts</th>
                  {!locked && <th scope="col">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {report.deductions.map((d) => {
                  const receipts = report.documents.filter(
                    (a) => a.deductionId === d.id && (a.role === 'receipt' || a.role === 'invoice'),
                  );
                  return (
                    <tr key={d.id}>
                      <td>{MOVE_OUT_CATEGORY_LABELS[d.category]}</td>
                      <td>
                        {d.description}
                        {d.isEstimate && (
                          <div>
                            <span className="badge">Estimate</span>
                          </div>
                        )}
                        {d.ownerLaborMinutes != null && d.ownerLaborRateCents != null && (
                          <div className="muted">
                            Owner labor: {(d.ownerLaborMinutes / 60).toFixed(2)} h × {formatCents(d.ownerLaborRateCents)}/h
                          </div>
                        )}
                      </td>
                      <td className="num">{formatCents(d.amountCents)}</td>
                      <td>
                        {receipts.length > 0 ? (
                          <ul style={{ margin: 0, paddingLeft: '1rem' }}>
                            {receipts.map((a) => (
                              <li key={a.id}>{a.document?.filename ?? a.documentId}</li>
                            ))}
                          </ul>
                        ) : (
                          <span className="muted">None</span>
                        )}
                        {!locked && <ReceiptUpload report={report} deduction={d} onSaved={setReport} />}
                      </td>
                      {!locked && (
                        <td>
                          <div className="row">
                            <button type="button" className="secondary" onClick={() => setDialog({ kind: 'deduction', deduction: d })}>
                              Edit<span className="visually-hidden"> {d.description}</span>
                            </button>
                            <button
                              type="button"
                              className="secondary"
                              disabled={removeDeduction.isPending}
                              onClick={() => {
                                if (window.confirm(`Delete the deduction “${d.description}”?`))
                                  removeDeduction.mutate({ reportId: report.id, id: d.id });
                              }}
                            >
                              Delete<span className="visually-hidden"> {d.description}</span>
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="mo-totals" className="stack">
        <h2 id="mo-totals">Totals</h2>
        <dl className="card kv-grid">
          <div>
            <dt className="muted">Deposit held</dt>
            <dd>{formatCents(report.totals.depositHeldCents)}</dd>
          </div>
          <div>
            <dt className="muted">Total deductions</dt>
            <dd>{formatCents(report.totals.totalDeductionsCents)}</dd>
          </div>
          <div>
            <dt className="muted">Deposit applied</dt>
            <dd>{formatCents(report.totals.depositAppliedCents)}</dd>
          </div>
          <div>
            <dt className="muted">{report.totals.balanceOwedCents > 0 ? 'Balance owed by tenants' : 'Refund due to tenants'}</dt>
            <dd>
              <strong>
                {formatCents(
                  report.totals.balanceOwedCents > 0 ? report.totals.balanceOwedCents : report.totals.refundDueCents,
                )}
              </strong>
            </dd>
          </div>
        </dl>
      </section>

      <section aria-labelledby="mo-photos" className="stack">
        <h2 id="mo-photos">Photos</h2>
        {detach.isError && <StatusMessage tone="err">{errorMessage(detach.error)}</StatusMessage>}
        {!locked && <PhotoUpload report={report} onSaved={setReport} />}
        {PHOTO_ROLES.map((role) => {
          const group = photos.filter((p) => p.role === role);
          if (group.length === 0) return null;
          return (
            <div key={role}>
              <h3>
                {MOVE_OUT_ROLE_LABELS[role]}s ({group.length})
              </h3>
              <ul
                style={{
                  listStyle: 'none',
                  padding: 0,
                  margin: 0,
                  display: 'grid',
                  gap: '0.75rem',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(11rem, 1fr))',
                }}
              >
                {group.map((p) => (
                  <li key={p.id} className="card" style={{ margin: 0, padding: '0.5rem' }}>
                    <DocThumb att={p} />
                    <p style={{ margin: '0.35rem 0 0' }}>
                      {p.room && <strong>{p.room}</strong>} {p.caption}
                    </p>
                    <p className="muted" style={{ margin: 0, fontSize: '0.85rem' }}>
                      {p.document?.capturedAt ? `Taken ${formatDate(p.document.capturedAt)}` : 'Date unknown'}
                    </p>
                    <div className="row">
                      {p.document?.derivedFromDocumentId && (
                        <button
                          type="button"
                          className="secondary"
                          disabled={download.isPending}
                          onClick={() => download.mutate(p.document!.derivedFromDocumentId!)}
                        >
                          Download original<span className="visually-hidden"> of {p.document.filename}</span>
                        </button>
                      )}
                      {!locked && (
                        <button
                          type="button"
                          className="secondary"
                          disabled={detach.isPending}
                          onClick={() => detach.mutate({ reportId: report.id, id: p.id })}
                        >
                          Remove<span className="visually-hidden"> photo {p.document?.filename}</span>
                        </button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
        {photos.length === 0 && <p className="muted">No photos yet. Move-in and move-out photos support each deduction.</p>}
      </section>

      {otherDocs.length > 0 && (
        <section aria-labelledby="mo-docs" className="stack">
          <h2 id="mo-docs">Receipts &amp; other documents</h2>
          <div className="table-wrap card" style={{ padding: 0 }}>
            <table>
              <caption className="visually-hidden">Receipts and other documents</caption>
              <thead>
                <tr>
                  <th scope="col">Type</th>
                  <th scope="col">File</th>
                  <th scope="col">For deduction</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {otherDocs.map((a) => (
                  <tr key={a.id}>
                    <td>{MOVE_OUT_ROLE_LABELS[a.role]}</td>
                    <td>{a.document?.filename ?? a.documentId}</td>
                    <td>{deductionLabel(a.deductionId)}</td>
                    <td>
                      <div className="row">
                        <button
                          type="button"
                          className="secondary"
                          disabled={download.isPending || !a.document}
                          onClick={() => download.mutate(a.documentId)}
                        >
                          Download<span className="visually-hidden"> {a.document?.filename}</span>
                        </button>
                        {a.document?.derivedFromDocumentId && (
                          <button
                            type="button"
                            className="secondary"
                            disabled={download.isPending}
                            onClick={() => download.mutate(a.document!.derivedFromDocumentId!)}
                          >
                            Download original<span className="visually-hidden"> of {a.document.filename}</span>
                          </button>
                        )}
                        {!locked && (
                          <button
                            type="button"
                            className="secondary"
                            disabled={detach.isPending}
                            onClick={() => detach.mutate({ reportId: report.id, id: a.id })}
                          >
                            Remove<span className="visually-hidden"> {a.document?.filename}</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section aria-labelledby="mo-statement" className="stack">
        <h2 id="mo-statement">Statement</h2>
        {report.blockers.map((b) => (
          <StatusMessage key={b.code} tone="err">
            {b.message}
          </StatusMessage>
        ))}
        {!locked &&
          report.warnings.map((w, i) => (
            <StatusMessage key={`${w.code}-${w.deductionId ?? i}`} tone="warn">
              {w.message}
            </StatusMessage>
          ))}
        {finalizeWarnings && finalizeWarnings.length > 0 && (
          <div className="status status--warn" role="status" aria-live="polite">
            <strong>Finalized with warnings:</strong>
            <ul>
              {finalizeWarnings.map((m) => (
                <li key={m}>{m}</li>
              ))}
            </ul>
          </div>
        )}
        {preview.isError && <StatusMessage tone="err">{errorMessage(preview.error)}</StatusMessage>}
        {finalize.isError && <StatusMessage tone="err">{errorMessage(finalize.error)}</StatusMessage>}
        {download.isError && <StatusMessage tone="err">{errorMessage(download.error)}</StatusMessage>}
        {share.isError && <StatusMessage tone="err">{errorMessage(share.error)}</StatusMessage>}
        {discard.isError && <StatusMessage tone="err">{errorMessage(discard.error)}</StatusMessage>}
        <div className="row">
          {!locked && (
            <>
              <button type="button" className="secondary" disabled={preview.isPending} onClick={() => preview.mutate(report.id)}>
                {preview.isPending ? 'Generating…' : 'Preview statement (PDF)'}
              </button>
              {isOwner && (
                <button
                  type="button"
                  disabled={finalize.isPending || report.blockers.length > 0}
                  onClick={() => {
                    const extra = report.warnings.length > 0 ? ` There ${report.warnings.length === 1 ? 'is 1 warning' : `are ${report.warnings.length} warnings`}.` : '';
                    if (window.confirm(`Finalize the statement? The report is locked afterwards.${extra}`))
                      finalize.mutate(report.id);
                  }}
                >
                  {finalize.isPending ? 'Finalizing…' : 'Finalize statement'}
                </button>
              )}
              {/* A reopened report has issued a statement — it can be amended, never discarded. */}
              {report.revision === 1 && (
                <button
                  type="button"
                  className="secondary"
                  disabled={discard.isPending}
                  onClick={() => {
                    if (window.confirm('Discard this draft move-out report?')) discard.mutate(report.id);
                  }}
                >
                  Discard draft
                </button>
              )}
            </>
          )}
          {locked && report.statementDocumentId && (
            <>
              <button
                type="button"
                disabled={download.isPending}
                onClick={() => download.mutate(report.statementDocumentId!)}
              >
                Download statement
              </button>
              {isOwner && (
                <button
                  type="button"
                  className="secondary"
                  disabled={share.isPending}
                  onClick={() =>
                    share.mutate({
                      reportId: report.id,
                      visibility: report.statementVisibility === 'tenant_visible' ? 'owner_only' : 'tenant_visible',
                    })
                  }
                >
                  {report.statementVisibility === 'tenant_visible'
                    ? 'Stop sharing with tenants'
                    : 'Share statement in tenant portal'}
                </button>
              )}
              {isOwner && (
                <button type="button" className="secondary" onClick={() => setReopenOpen(true)}>
                  Reopen for amendment
                </button>
              )}
            </>
          )}
        </div>
        {!locked && !isOwner && (
          <p className="muted">
            Only the owner can finalize the statement. You can prepare the draft and preview it.
          </p>
        )}
        {locked && (
          <p className="muted">
            {report.statementVisibility === 'tenant_visible'
              ? 'Tenants on this lease can see the statement in their portal.'
              : 'Only you can see the statement. Mail it (with any enclosures) to each recipient, and/or sign and email it below — sharing it in the portal is optional.'}
          </p>
        )}
        {!locked && report.revision > 1 && (
          <p className="muted">
            This report was reopened for amendment. Finalize it again to issue revision {report.revision}
            {report.amended ? ' — it will be titled “Amended Itemized Statement”, because an earlier revision was already delivered' : ''}
            ; it must then be signed again before it can be emailed.
          </p>
        )}
        {report.revisions.length > 0 && (
          <div className="stack">
            <h3 id="mo-revisions">Revision history</h3>
            <ul aria-labelledby="mo-revisions">
              {report.revisions.map((rev) => (
                <li key={rev.id}>
                  Revision {rev.revision} — finalized {formatDate(rev.finalizedAt)} · total deductions{' '}
                  {rev.totalDeductionsCents != null ? formatCents(rev.totalDeductionsCents) : '—'} · delivered:{' '}
                  {rev.wasDelivered ? 'yes' : 'no'} · superseded {formatDate(rev.reopenedAt)}{' '}
                  <button
                    type="button"
                    className="secondary"
                    disabled={download.isPending}
                    onClick={() => download.mutate(rev.statementDocumentId)}
                  >
                    Superseded statement (revision {rev.revision})
                  </button>
                  <br />
                  <span className="muted">Reason: {rev.reason}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </section>

      {locked && <SignAndSend report={report} isOwner={isOwner} ownerEmail={ownerEmail} />}

      {reopenOpen && locked && isOwner && (
        <ReopenDialog
          report={report}
          onClose={() => setReopenOpen(false)}
          onReopened={(r) => {
            setReport(r);
            setFinalizeWarnings(null);
            // The signing / delivery state of the superseded revision is stale now.
            queryClient.removeQueries({ queryKey: deliveryKey(report.id) });
            void queryClient.invalidateQueries({ queryKey: ['admin', 'documents'] });
          }}
        />
      )}

      {dialog?.kind === 'recipient' && (
        <RecipientDialog
          report={report}
          lease={lease}
          recipient={dialog.recipient}
          onClose={() => setDialog(null)}
          onSaved={setReport}
        />
      )}
      {dialog?.kind === 'deduction' && (
        <DeductionDialog
          report={report}
          deduction={dialog.deduction}
          onClose={() => setDialog(null)}
          onSaved={setReport}
        />
      )}
    </>
  );
}
